import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { NavLink, useParams } from "react-router-dom";
import { Button, Spinner, Switch, Textarea, Input, Chip } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useStyles } from "@/hooks/useStyles";

import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";
import { IoIosArrowBack } from "react-icons/io";

type CustomerGroup = {
  customer_group_id: string;
  customer_group_alias?: string;
  source?: string;
  status?: string;
};

type CustomerSettingsType = {
  customer_id: string;
  customer_name: string;
  customer_instruction: string;
  status_automation: string;
  force_hitl: boolean;
};

interface Params {
  [key: string]: string | undefined;
  userId: string;
}

type FormValues = {
  customer_id: string;
  customer_name: string;
  customer_instruction: string;
  status_automation: string;
  force_hitl: boolean;
};

export default function CustomerEditSettings() {
  const { t } = useTranslation();
  const styles = useStyles();
  const params = useParams<Params>();
  const axiosPrivate = useAxiosPrivate();

  const [submitting, setSubmitting] = useState(false);

  const { data: customerGroups = [], isLoading: groupsLoading } = useQuery<
    CustomerGroup[]
  >({
    queryKey: ["CustomerGroups", params.customerId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/customers/settings/${params.customerId}/groups`,
      );
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });
  const [dataCustomerSettings, setDataCustomerSettings] =
    useState<CustomerSettingsType | null>(null);
  const methods = useForm<FormValues>({
    mode: "onBlur",
  });

  useEffect(() => {
    const fetchCustomerSettings = async () => {
      try {
        const response = await axiosPrivate.get(
          `/customers/settings/${params.customerId}`,
        );
        const customerSettingsData = response.data;
        setDataCustomerSettings(customerSettingsData);

        methods.reset({
          ...customerSettingsData,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données client :",
          error,
        );
      }
    };
    fetchCustomerSettings();
  }, [params.customerId, axiosPrivate, methods]);

  const onSubmit = async (formData: any) => {
    setSubmitting(true);
    const formattedData = {
      status_automation: formData.status_automation,
      customer_instruction: formData.customer_instruction,
      force_hitl: formData.force_hitl
    };
    await axiosPrivate.put(`/customers/settings/${params.customerId}`, formattedData);
    setSubmitting(false);
  };

  return (
    <>
      <div className="w-full text-vistagrey my-6 inline-flex">
        <div>
          <div className="text-vistagrey text-xl">
            <AiOutlineUsergroupAdd className="fill-vistagreen inline-block text-4xl mr-2" />
            {t("customer_settings")}
          </div>
          <div className="ml-10">
            <NavLink
              to={`/customers/`}
            >
              <Button
                color="primary"
                variant="bordered"
                size="sm"
                radius="full"
                startContent={<IoIosArrowBack />}
              >
                {t("back")}
              </Button>
            </NavLink>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg w-full">
        {dataCustomerSettings && (
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col items-start gap-4"
            >
              <div className="relative w-full">
                <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold">
                  {t("edit_customer_settings")}
                </div>
                <div className="space-y-6 p-5">
                  <Controller
                    name="customer_id"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_id}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        label={t("customer_id")}
                        placeholder={t("customer_id")}
                        variant="flat"
                        labelPlacement="outside"
                        readOnly
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="customer_name"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_name}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        label={t("customer_name")}
                        placeholder={t("customer_name")}
                        variant="flat"
                        labelPlacement="outside"
                        readOnly
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="status_automation"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.status_automation}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size="md"
                        color="primary"
                        startContent={<MdOutlineAutoMode />}
                        endContent={<CgUnavailable />}
                        isSelected={value === "auto"}
                        onValueChange={(isSelected) =>
                          onChange(isSelected ? "auto" : "pending_review")
                        }
                        classNames={styles}
                        className="mr-4"
                      >
                        {t("mode_auto")}
                      </Switch>
                    )}
                  />

                  <Controller
                    name="force_hitl"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.force_hitl}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size="md"
                        color="primary"
                        startContent={<MdOutlineAutoMode />}
                        endContent={<CgUnavailable />}
                        isSelected={value === true}
                        onValueChange={(isSelected) =>
                          onChange(isSelected)
                        }
                        classNames={styles}
                      >
                        {t("customer_to_verify")}
                      </Switch>
                    )}
                  />

                  <Controller
                    name="customer_instruction"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_instruction}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        label={t("instructions")}
                        placeholder={t("enter_your_instructions")}
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Button
                    type="submit"
                    className="bg-vistagreen text-white rounded-md p-3"
                  >
                    {submitting && <Spinner color="default" />}
                    {!submitting && <div>{t("validate_changes")}</div>}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </div>

      <div className="bg-white rounded-lg w-full mt-6">
        <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold">
          {t("customer_groups")}
        </div>
        <div className="p-5">
          {groupsLoading && <Spinner size="sm" color="primary" />}
          {!groupsLoading && customerGroups.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {customerGroups.map((group) => (
                <Chip
                  key={group.customer_group_id}
                  size="sm"
                  variant="flat"
                  color="default"
                >
                  {group.customer_group_alias || group.customer_group_id}
                </Chip>
              ))}
            </div>
          )}
          {!groupsLoading && customerGroups.length === 0 && (
            <div className="text-sm text-default-400 italic">
              {t("customer_no_group")}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
